/**
 * /admin/commissions — RM commissions (P26, team feedback fb 27). Admin-only (Pedro
 * 2026-10-10: "RM Commissions are Admin only"). Design of record:
 * `_MEMORY/decisions/2026-10-10_rm-commissions-design.md`.
 *
 * Setup tab (increment 1): define commission tables — marginal bands on a person's ANNUAL
 * credited fees, edited with the same band editor as the client fee schedules — and
 * assign one table to each person. Fee split tab (increment 2, moved here 2026-10-10 and
 * decoupled from Named RMs): per agreement, who is credited with how much of its billed fee —
 * any user. Monthly tab (increment 3, Pedro 2026-10-10): the month's commissions computed when
 * read from the frozen fee runs + ad-hoc fees; fees with no split pause on "Proceed or Correct?".
 */
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Container, Group, Loader, Modal,
  SegmentedControl, Select, Stack, Table, Tabs, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconChevronDown, IconChevronRight, IconDownload, IconPencil, IconPlus,
  IconRefresh, IconSearch, IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { commissionsApi, type CommissionTable, type FeeSplitRow } from '../api/commissions';
import { bpsToPct, fmtEurFromCents } from '../api/feeSchedules';
import { useAlertModal } from '../components/AlertModalProvider';
import { FeeSplitModal } from '../components/FeeSplitModal';
import { RequireAccess } from '../components/RequireAccess';
import {
  TrancheBandEditor, bandsFromTranches, buildBandsIn, emptyBands, validateBandDrafts,
  type BandDraft,
} from '../components/TrancheBandEditor';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const KEY = ['commissions'] as const;

function bandsSummary(t: CommissionTable): string {
  return t.tranches.map((b) => (b.max_eur_cents === null
    ? `above €${fmtEurFromCents(b.min_eur_cents)}: ${bpsToPct(b.rate_bps)}%`
    : `€${fmtEurFromCents(b.min_eur_cents)}–€${fmtEurFromCents(b.max_eur_cents)}: ${bpsToPct(b.rate_bps)}%`))
    .join(' · ');
}

export function RmCommissionsPage() {
  useDocumentTitle('RM commissions');
  const [tab, setTab] = useState<string | null>('setup');
  // ONE month for the Fee split and Monthly tabs (smoke 2026-10-11, S12): the split shown,
  // the split editor's "From" and the commissions computed all refer to the same month.
  const [month, setMonth] = useState<string>(previousMonth());
  return (
    <RequireAccess roles={['admin']} message="RM commissions are restricted to Admins.">
      <Container size="xl">
        <Stack>
          <Title order={2}>RM commissions</Title>
          <Text c="dimmed" size="sm">
            A person&apos;s commission is a percentage of the billed fees credited to them by each
            agreement&apos;s fee split. The percentage comes from their commission table, applied
            to their annual credited fees (current run-rate) like the client fee tranches.
          </Text>
          <Tabs value={tab} onChange={setTab}>
            <Tabs.List>
              <Tabs.Tab value="setup">Setup</Tabs.Tab>
              <Tabs.Tab value="split">Fee split</Tabs.Tab>
              <Tabs.Tab value="monthly">Monthly</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="setup" pt="md">
              <Stack>
                <TablesCard />
                <AssignmentsCard />
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="split" pt="md">
              <FeeSplitTab month={month} onMonth={setMonth} />
            </Tabs.Panel>
            <Tabs.Panel value="monthly" pt="md">
              <MonthlyTab month={month} onMonth={setMonth} onCorrect={setTab} />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </RequireAccess>
  );
}

function TablesCard() {
  const qc = useQueryClient();
  const { confirm, showAlert } = useAlertModal();
  const tablesQ = useQuery({ queryKey: [...KEY, 'tables'], queryFn: commissionsApi.tables });
  const [editing, setEditing] = useState<CommissionTable | 'new' | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => commissionsApi.deleteTable(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      notifications.show({ color: 'green', message: 'Commission table deleted' });
    },
    onError: (e) => showAlert({ color: 'red', title: 'Could not delete',
      message: e instanceof ApiError ? e.detail : String(e) }),
  });

  async function onDelete(t: CommissionTable) {
    if (await confirm({ title: 'Delete commission table?', message: `Delete "${t.name}"?`,
      confirmLabel: 'Delete', destructive: true })) del.mutate(t.id);
  }

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group justify="space-between">
          <Title order={4}>Commission tables</Title>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')}>
            New table
          </Button>
        </Group>
        {tablesQ.data?.length === 0 && (
          <Text size="sm" c="dimmed">No commission tables yet. Create one, then assign it to RMs below.</Text>
        )}
        {(tablesQ.data?.length ?? 0) > 0 && (
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Bands (annual credited fees)</Table.Th>
                <Table.Th w={110} ta="center">Assigned to</Table.Th>
                <Table.Th w={90} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tablesQ.data?.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{t.name}</Text>
                    {t.description && <Text size="xs" c="dimmed">{t.description}</Text>}
                  </Table.Td>
                  <Table.Td><Text size="xs">{bandsSummary(t)}</Text></Table.Td>
                  <Table.Td ta="center">
                    <Badge variant="light" color={t.assigned_count ? 'blue' : 'gray'}>
                      {t.assigned_count} RM{t.assigned_count === 1 ? '' : 's'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      <Tooltip label="Edit" withArrow>
                        <Button size="compact-xs" variant="subtle" onClick={() => setEditing(t)}>
                          <IconPencil size={14} />
                        </Button>
                      </Tooltip>
                      <Tooltip withArrow label={t.assigned_count
                        ? 'Assigned to RMs — assign them another table first' : 'Delete'}>
                        <span>
                          <Button size="compact-xs" variant="subtle" color="red"
                            disabled={t.assigned_count > 0} onClick={() => onDelete(t)}>
                            <IconTrash size={14} />
                          </Button>
                        </span>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
      <TableEditor editing={editing} onClose={() => setEditing(null)} />
    </Card>
  );
}

function TableEditor({ editing, onClose }: {
  editing: CommissionTable | 'new' | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const isNew = editing === 'new';
  const current = editing && editing !== 'new' ? editing : null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [bands, setBands] = useState<BandDraft[]>(emptyBands());
  const [error, setError] = useState<string | null>(null);
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  // Reset the draft each time the modal opens on a different target.
  const target = editing === null ? null : isNew ? 'new' : current!.id;
  if (target !== openedFor) {
    setOpenedFor(target);
    setName(current?.name ?? '');
    setDescription(current?.description ?? '');
    setBands(current ? bandsFromTranches(current.tranches) : emptyBands());
    setError(null);
  }

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description: description.trim() || null, tranches: buildBandsIn(bands) };
      return isNew ? commissionsApi.createTable(body) : commissionsApi.updateTable(current!.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      notifications.show({ color: 'green', message: isNew ? 'Commission table created' : 'Commission table saved' });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.detail : String(e)),
  });

  function onSave() {
    if (!name.trim()) { setError('A name is required.'); return; }
    const bandError = validateBandDrafts(bands);
    if (bandError) { setError(bandError); return; }
    setError(null);
    save.mutate();
  }

  return (
    <Modal opened={editing !== null} onClose={onClose} size="lg"
      title={isNew ? 'New commission table' : 'Edit commission table'}>
      <Stack>
        <TextInput label="Name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput label="Description" value={description}
          onChange={(e) => setDescription(e.currentTarget.value)} />
        <Text size="sm" c="dimmed">
          Bands apply to the RM&apos;s annual credited fees, marginally: each rate applies only to
          the part of the fees inside its band.
        </Text>
        <TrancheBandEditor bands={bands} onChange={setBands} />
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} loading={save.isPending}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Monthly tab — the month's commissions, computed when read (increment 3) ──────────────
const eur = (cents: number): string => `€${fmtEurFromCents(cents)}`;

function previousMonth(): string {
  const d = new Date();
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-01`;
}

function monthName(iso: string): string {
  return new Date(`${iso.slice(0, 7)}-01T00:00:00`)
    .toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/** The page's month picker, shared by the Fee split and Monthly tabs. */
function PageMonthPicker({ month, onMonth }: { month: string; onMonth: (m: string) => void }) {
  return (
    <MonthPickerInput label="Month" w={200} value={new Date(`${month}T00:00:00`)}
      onChange={(d) => d && onMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`)} />
  );
}

function MonthlyTab({ month, onMonth, onCorrect }: {
  month: string; onMonth: (m: string) => void; onCorrect: (tab: string) => void;
}) {
  // Pedro 2026-10-10: fees with no fee split PAUSE the calculation until "Proceed or Correct?".
  // Proceed is remembered for the month it was given on only; Undo or Recalculate clears it.
  const [proceededFor, setProceededFor] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({ queryKey: [...KEY, 'monthly', month],
    queryFn: () => commissionsApi.monthly(month) });
  function recalculate() {
    setProceededFor(null);
    void q.refetch();
  }
  const r = q.data;
  const blocked = !!r && r.problems.length > 0;
  const paused = !!r && !blocked && r.uncredited.length > 0 && proceededFor !== month;
  const uncreditedTotal = (r?.uncredited ?? []).reduce((s, u) => s + u.billed_cents, 0);
  const exportReason = !r ? 'Loading…'
    : blocked ? 'Correct the problems first'
      : paused ? 'Choose Proceed or Correct first'
        : r.people.length === 0 ? 'No commissions this month' : undefined;

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        For the month picked, each person&apos;s commission basis is their share (Fee split) of
        the fees billed that month: the frozen fee runs, ex-VAT, plus ad-hoc fees, with credit
        notes subtracting. The rate comes from their commission table applied to that
        month&apos;s fees annualised: × 12 for monthly billing, × 4 quarterly, × 2 semiannual.
        Figures update whenever a fee run changes.
      </Text>
      <Group align="flex-end" justify="space-between">
        <Group align="flex-end">
          <PageMonthPicker month={month} onMonth={(m) => { setProceededFor(null); onMonth(m); }} />
          <Tooltip label="Compute the month again from the current fee runs and fee splits" withArrow>
            <Button variant="default" leftSection={<IconRefresh size={16} />}
              loading={q.isFetching} onClick={recalculate}>
              Recalculate
            </Button>
          </Tooltip>
        </Group>
        <Tooltip label={exportReason} disabled={!exportReason} withArrow>
          <span>
            <Button variant="default" leftSection={<IconDownload size={16} />} component="a"
              href={commissionsApi.monthlyXlsxHref(month, proceededFor === month)}
              disabled={!!exportReason}>
              Export to Excel
            </Button>
          </span>
        </Tooltip>
      </Group>

      {q.isLoading && <Loader size="sm" />}
      {q.error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Could not compute the month. {q.error instanceof Error ? q.error.message : ''}
        </Alert>
      )}

      {blocked && r && (
        <Alert color="red" title="Correct these first — the calculation is stopped">
          <Stack gap={4}>
            {r.problems.map((p) => <Text key={p} size="sm">{p}</Text>)}
            <Group gap="xs" mt="xs">
              <Button size="xs" variant="white" color="red" onClick={() => onCorrect('split')}>
                Open Fee split
              </Button>
              <Button size="xs" variant="white" color="red" onClick={() => onCorrect('setup')}>
                Open Setup
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {paused && r && (
        <Alert color="red" title={`Fees with no fee split in ${monthName(month)}: Proceed or Correct?`}>
          <Stack gap={4}>
            <Text size="sm">
              {r.uncredited.length} agreement{r.uncredited.length === 1 ? '' : 's'} billed{' '}
              {eur(uncreditedTotal)} with no fee split. If you proceed, those fees are credited to
              nobody for this month. To correct, set their fee split and come back.
            </Text>
            {r.uncredited.map((u) => (
              <Text key={u.agreement_id} size="sm">
                · {u.agreement_label}: {eur(u.billed_cents)}
                {u.named_rms.length > 0 ? ` (Named RMs: ${u.named_rms.join(', ')})` : ' (no Named RM)'}
                {u.later_split_from && (
                  <Text span size="sm" fw={600}>
                    {' '}— its fee split starts only in {monthName(u.later_split_from)}, after this month
                  </Text>
                )}
              </Text>
            ))}
            <Group gap="xs" mt="xs">
              <Button size="xs" color="red" onClick={() => setProceededFor(month)}>Proceed</Button>
              <Button size="xs" variant="white" color="red" onClick={() => onCorrect('split')}>
                Correct
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {r && !blocked && !paused && (
        <>
          {r.uncredited.length > 0 && (
            <Alert color="orange" variant="light">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Text size="sm">
                  Proceeding: {eur(uncreditedTotal)} billed by {r.uncredited.length} agreement
                  {r.uncredited.length === 1 ? '' : 's'} with no fee split is credited to nobody this
                  month ({r.uncredited.map((u) => u.agreement_label).join(', ')}).
                </Text>
                <Button size="xs" variant="white" color="orange" onClick={() => setProceededFor(null)}>
                  Undo
                </Button>
              </Group>
            </Alert>
          )}
          {r.people.length === 0 ? (
            <Text size="sm" c="dimmed">No commissions for {monthName(month)}.</Text>
          ) : (
            <Table withTableBorder striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <Table.Th>Person</Table.Th>
                  <Table.Th ta="right">Fees credited</Table.Th>
                  <Table.Th ta="right">Annual base</Table.Th>
                  <Table.Th ta="right">Effective rate</Table.Th>
                  <Table.Th ta="right">Commission</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {r.people.map((p) => (
                  <Fragment key={p.user_id}>
                    <Table.Tr>
                      <Table.Td>
                        <ActionIcon variant="subtle" size="sm"
                          aria-label={open === p.user_id ? 'Hide agreements' : 'Show agreements'}
                          onClick={() => setOpen(open === p.user_id ? null : p.user_id)}>
                          {open === p.user_id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                        </ActionIcon>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{p.user_label}</Text>
                        <Text size="xs" c="dimmed">{p.table_name ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td ta="right">{eur(p.credited_cents)}</Table.Td>
                      <Table.Td ta="right">{eur(p.annual_base_cents)}</Table.Td>
                      <Table.Td ta="right">
                        {p.effective_rate_pct === null ? '—' : `${p.effective_rate_pct.toFixed(3)}%`}
                      </Table.Td>
                      <Table.Td ta="right" fw={600}>{eur(p.commission_cents)}</Table.Td>
                    </Table.Tr>
                    {open === p.user_id && p.lines.map((l) => (
                      <Table.Tr key={l.agreement_id}>
                        <Table.Td />
                        <Table.Td>
                          <Text size="xs">{l.agreement_label} · share {l.share_bps / 100}%</Text>
                          <Text size="xs" c="dimmed">
                            Agreement fees {eur(l.billed_cents)} · annualised {eur(l.annualized_cents)}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right"><Text size="xs">{eur(l.credited_cents)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{eur(l.annual_base_cents)}</Text></Table.Td>
                        <Table.Td />
                        <Table.Td />
                      </Table.Tr>
                    ))}
                  </Fragment>
                ))}
                <Table.Tr>
                  <Table.Td />
                  <Table.Td><Text size="sm" fw={600}>Total</Text></Table.Td>
                  <Table.Td ta="right" fw={600}>{eur(r.total_credited_cents)}</Table.Td>
                  <Table.Td />
                  <Table.Td />
                  <Table.Td ta="right" fw={600}>{eur(r.total_commission_cents)}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          )}
          <Text size="xs" c="dimmed">
            Fees billed in {monthName(month)}: {eur(r.total_billed_cents)}. Ad-hoc fees and credit
            notes count in the month&apos;s fees as they are, and once (not annualised) in the
            annual base.
          </Text>
        </>
      )}
    </Stack>
  );
}

// ── Fee split tab — organised like Admin → Named RMs: a filterable table of every agreement,
//    each row's "Manage" opening the split editor (Pedro 2026-10-10). ─────────────────────────
function FeeSplitStatusBadge({ status }: { status: string }) {
  const color = status === 'Active' ? 'green' : status === 'Prospect' ? 'grape' : 'gray';  // P37: blue means "pending approval"
  return <Badge color={color} variant="light">{status}</Badge>;
}

function FeeSplitTab({ month, onMonth }: { month: string; onMonth: (m: string) => void }) {
  const feedQ = useQuery({ queryKey: [...KEY, 'fee-splits', month],
    queryFn: () => commissionsApi.feeSplits(month) });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [splitFilter, setSplitFilter] = useState('all');
  const [manageRow, setManageRow] = useState<FeeSplitRow | null>(null);

  const q = search.trim().toLowerCase();
  const rows = (feedQ.data ?? []).filter((r) => {
    if (q && !r.agreement_label.toLowerCase().includes(q)) return false;
    if (statusFilter !== 'all' && r.agreement_status !== statusFilter) return false;
    if (splitFilter === 'set' && r.in_force.length === 0) return false;
    if (splitFilter === 'none' && r.in_force.length > 0) return false;
    if (splitFilter === 'problems' && !r.problem) return false;
    return true;
  });

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        For each agreement, who is credited with how much of its billed fee — anyone, not only
        its Named RMs (e.g. a referral or client servicing). Splits are dated: a change applies
        from the month chosen. A problem in red stops the monthly compute until fixed. The
        month below is the same as on the Monthly tab.
      </Text>
      <Group align="flex-end" wrap="wrap" gap="md">
        <PageMonthPicker month={month} onMonth={onMonth} />
        <TextInput label="Search" placeholder="Code or mnemonic" leftSection={<IconSearch size={14} />}
          value={search} onChange={(e) => setSearch(e.currentTarget.value)} maw={260} />
        <div>
          <Text size="xs" fw={500} mb={4}>Status</Text>
          <SegmentedControl size="xs" value={statusFilter} onChange={setStatusFilter}
            data={[
              { label: 'All', value: 'all' }, { label: 'Active', value: 'Active' },
              { label: 'Inactive', value: 'Inactive' }, { label: 'Prospect', value: 'Prospect' },
            ]} />
        </div>
        <div>
          <Text size="xs" fw={500} mb={4}>Fee split</Text>
          <SegmentedControl size="xs" value={splitFilter} onChange={setSplitFilter}
            data={[
              { label: 'All', value: 'all' }, { label: 'Set', value: 'set' },
              { label: 'Not set', value: 'none' }, { label: 'Problems', value: 'problems' },
            ]} />
        </div>
      </Group>
      {feedQ.isLoading ? <Loader size="sm" /> : feedQ.error ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Could not load the fee splits. {feedQ.error instanceof Error ? feedQ.error.message : ''}
        </Alert>
      ) : (
        <>
          <Text size="xs" c="dimmed">{rows.length} agreement{rows.length === 1 ? '' : 's'}</Text>
          <Table withTableBorder striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Agreement</Table.Th>
                <Table.Th w={110}>Status</Table.Th>
                <Table.Th>Named RMs</Table.Th>
                <Table.Th>Fee split in {monthName(month)}</Table.Th>
                <Table.Th w={110} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.agreement_id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/agreements/${r.agreement_id}`} size="sm" fw={500}>
                      {r.agreement_label}
                    </Anchor>
                  </Table.Td>
                  <Table.Td><FeeSplitStatusBadge status={r.agreement_status} /></Table.Td>
                  <Table.Td>
                    <Text size="sm" c={r.named.length ? undefined : 'dimmed'}>
                      {r.named.length
                        ? r.named.map((n) => `${n.user_label}${n.is_primary ? ' ★' : ''}`).join(', ')
                        : '— none —'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {r.in_force.length > 0 ? (
                      <Group gap={6}>
                        {r.in_force.map((s) => (
                          <Badge key={s.user_id} variant="light" color="blue">
                            {s.user_label} {s.share_bps / 100}%
                          </Badge>
                        ))}
                      </Group>
                    ) : <Text size="sm" c="dimmed">Not set</Text>}
                    {r.problem && <Text size="xs" c="red" mt={4}>{r.problem}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Button size="xs" variant="light" onClick={() => setManageRow(r)}>Manage</Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="dimmed" ta="center" py="md">No agreements match the filters.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </>
      )}
      {manageRow && <FeeSplitModal row={manageRow} month={month} onClose={() => setManageRow(null)} />}
    </Stack>
  );
}

function AssignmentsCard() {
  const qc = useQueryClient();
  const { showAlert } = useAlertModal();
  const rowsQ = useQuery({ queryKey: [...KEY, 'assignments'], queryFn: commissionsApi.assignments });
  const tablesQ = useQuery({ queryKey: [...KEY, 'tables'], queryFn: commissionsApi.tables });
  const options = (tablesQ.data ?? []).map((t) => ({ value: t.id, label: t.name }));
  const assign = useMutation({
    mutationFn: ({ userId, tableId }: { userId: string; tableId: string | null }) =>
      commissionsApi.assign(userId, tableId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      notifications.show({ color: 'green', message: 'Commission table assignment saved' });
    },
    onError: (e) => showAlert({ color: 'red', title: 'Could not save',
      message: e instanceof ApiError ? e.detail : String(e) }),
  });

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Title order={4}>Commission table per person</Title>
        <Text size="sm" c="dimmed">
          People in a fee split or named as RMs are listed first. Someone in a fee split without
          a table stops the monthly compute until one is assigned.
        </Text>
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th w={130} ta="center">In fee splits</Table.Th>
              <Table.Th w={130} ta="center">Named RM on</Table.Th>
              <Table.Th w={280}>Commission table</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rowsQ.data?.map((r) => (
              <Table.Tr key={r.user_id}>
                <Table.Td>
                  <Text size="sm">{r.user_label}</Text>
                  <Text size="xs" c="dimmed">{r.role}</Text>
                </Table.Td>
                <Table.Td ta="center">
                  <Text size="sm" c={r.split_agreements ? undefined : 'dimmed'}>
                    {r.split_agreements} agreement{r.split_agreements === 1 ? '' : 's'}
                  </Text>
                </Table.Td>
                <Table.Td ta="center">
                  <Text size="sm" c={r.named_agreements ? undefined : 'dimmed'}>
                    {r.named_agreements} agreement{r.named_agreements === 1 ? '' : 's'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Select size="xs" data={options} value={r.table_id} clearable
                    placeholder={options.length ? 'No table' : 'Create a table first'}
                    disabled={!options.length || assign.isPending}
                    onChange={(v) => assign.mutate({ userId: r.user_id, tableId: v })}
                    comboboxProps={{ withinPortal: true }} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
